import React from 'react';
import { Box, VStack, HStack, Text, Icon, Image } from '@chakra-ui/react';
import { FiMail, FiUser, FiLogOut } from 'react-icons/fi';
import { LuWallet, LuMessageCircleQuestion } from 'react-icons/lu';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { useLogout } from '../../hooks/useLogout';

interface DesktopSidebarProps {}

interface MenuItemProps {
    icon: any;
    label: string;
    isActive: boolean;
    onClick: () => void;
    counter?: number;
}

const MenuItem: React.FC<MenuItemProps> = ({ icon, label, isActive, onClick, counter }) => {
    return (
        <Box
            as="button"
            w="full"
            p={4}
            pl={6}
            borderRadius={0}
            bg={isActive ? 'yellow.600' : 'transparent'}
            cursor="pointer"
            _hover={{
                bg: isActive ? 'yellow.700' : 'gray.200',
            }}
            transition="all 0.3s ease-in-out"
            onClick={onClick}
            position="relative"
            overflow="hidden"
        >
            <HStack gap={4} justify="space-between" position="relative" zIndex={1}>
                <HStack gap={4}>
                    <Icon
                        as={icon}
                        boxSize={5}
                        color={isActive ? 'black' : 'gray.600'}
                        transition="color 0.3s ease-in-out"
                    />
                    <Text
                        fontSize="md"
                        fontWeight={isActive ? 'semibold' : 'medium'}
                        color={isActive ? 'black' : 'gray.600'}
                        transition="all 0.3s ease-in-out"
                    >
                        {label}
                    </Text>
                </HStack>
                {counter && counter > 0 && (
                    <Box
                        bg="red.100"
                        color="red.600"
                        fontSize="xs"
                        fontWeight="bold"
                        px={2}
                        py={1}
                        borderRadius="full"
                        minW={6}
                        textAlign="center"
                        transition="all 0.3s ease-in-out"
                    >
                        {counter}
                    </Box>
                )}
            </HStack>
        </Box>
    );
};

const DesktopSidebar: React.FC<DesktopSidebarProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { logout, isLoading: isLoggingOut } = useLogout();

    const menuItems = [
        { icon: FiMail, label: t.sidebar.menu.mail, path: '/mail' },
        { icon: HiOutlineOfficeBuilding, label: t.sidebar.menu.office, path: '/office' },
        { icon: LuWallet, label: t.sidebar.menu.payment, path: '/payment' },
        { icon: LuMessageCircleQuestion, label: t.sidebar.menu.support, path: '/support' },
        { icon: FiUser, label: t.sidebar.menu.profile, path: '/profile' },
    ];

    const handleLogout = () => {
        if (isLoggingOut) return;
        logout();
    };

    return (
        <Box
            w="280px"
            h="100vh"
            bg="white"
            boxShadow="4px 0 20px rgba(0, 0, 0, 0.1)"
            position="fixed"
            left={0}
            top={0}
            zIndex={1000}
        >
            <VStack gap={0} align="stretch" h="full">
                {/* Header */}
                <Box p={6} borderBottom="1px solid" borderColor="gray.100">
                    <HStack gap={3}>
                        <Box
                            w={10}
                            h={10}
                            borderRadius="lg"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            overflow="hidden"
                        >
                            <Image
                                src="/favicon-192x192.png"
                                alt="Office Hof"
                                boxSize={12}
                                objectFit="contain"
                            />
                        </Box>
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                            {t.sidebar.appTitle}
                        </Text>
                    </HStack>
                </Box>

                {/* Menu Items */}
                <VStack gap={0} flex={1}>
                    {menuItems.map((item) => (
                        <MenuItem
                            key={item.path}
                            icon={item.icon}
                            label={item.label}
                            isActive={
                                location.pathname === item.path ||
                                location.pathname.startsWith(item.path + '/')
                            }
                            onClick={() => navigate(item.path)}
                        />
                    ))}
                </VStack>

                {/* Logout Button */}
                <Box pt={8}>
                    <Box
                        as="button"
                        w="full"
                        p={4}
                        pl={6}
                        borderRadius={0}
                        bg="transparent"
                        cursor="pointer"
                        _hover={{
                            bg: 'red.50',
                            transition: 'background-color 0.2s ease-in-out',
                        }}
                        transition="background-color 0.2s ease-in-out"
                        onClick={handleLogout}
                    >
                        <HStack gap={4}>
                            <Icon as={FiLogOut} boxSize={5} color="red.500" transition="color 0.2s" />
                            <Text fontSize="md" fontWeight="medium" color="red.500" transition="all 0.2s">
                                {t.sidebar.menu.logout}
                            </Text>
                        </HStack>
                    </Box>
                </Box>
            </VStack>
        </Box>
    );
};

export default DesktopSidebar;
